import React, { ChangeEvent, useRef, useState } from 'react';
import { CustomTextField, VerificationCodeInputContainer } from './VerificationCodeInput.styles';


export interface CustomVerificationCodeInputProps {
    inputLength: number;
    getConcatenatedValues: Function;
    hasError: Function;
    errorFromOutside?: boolean;
}

export const VerificationCodeInput = ({
    inputLength,
    getConcatenatedValues,
    hasError,
    errorFromOutside = false,
    ...rest
}: CustomVerificationCodeInputProps) => {

    const inputRefs = Array.from({ length: inputLength }, () => useRef<HTMLInputElement>());
    const [values, setValues] = useState(Array(inputLength).fill(''));
    const [errors, setErrors] = useState(Array(inputLength).fill(false));

    const fillFromIndex = (index: number, chars: string[]) => {
        const newValues = [...values];
        const newErrors = [...errors];

        chars.forEach((char, i) => {
            const targetIndex = index + i;
            if (targetIndex < inputLength) {
                newValues[targetIndex] = char;
                newErrors[targetIndex] = false;
                if (inputRefs[targetIndex].current) {
                    inputRefs[targetIndex].current!.value = char;
                }
            }
        });

        const lastFilledIndex = Math.min(index + chars.length - 1, inputLength - 1);
        if (chars.length === 1 && lastFilledIndex < inputLength - 1) {
            inputRefs[lastFilledIndex + 1].current?.focus();
        } else {
            inputRefs[lastFilledIndex].current?.focus();
        }

        setValues(newValues);
        setErrors(newErrors);
        getConcatenatedValues(newValues.join(''));
        hasError(newErrors.some(error => error === true));
    };

    const handleInputChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        const chars = event.target.value.slice(0, inputLength - index).split('');
        if (chars.length > 0) fillFromIndex(index, chars);
    };

    const handlePaste = (index: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const chars = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, inputLength - index).split('');
        if (chars.length > 0) fillFromIndex(index, chars);
    };

    const handleBlur = (index: number) => (event: any) => {
        const newErrors = [...errors];

        if (values[index].trim() === '') {
            newErrors[index] = true;
        } else {
            newErrors[index] = false;
        }

        setErrors(newErrors);
        hasError(newErrors.some(error => error === true))
    };

    return (
        <VerificationCodeInputContainer data-cy={`verification-code-input-container`}>
            {inputRefs.map((ref, index) =>
                <CustomTextField
                    data-cy={`verification-code-${index}`}
                    required
                    key={index}
                    inputRef={ref}
                    inputProps={{ min: 0, style: { textAlign: 'center' }, maxLength: 1 }}
                    error={errors[index]}
                    onChange={handleInputChange(index)}
                    onBlur={handleBlur(index)}
                    onPaste={handlePaste(index)}/>
            )}

        </VerificationCodeInputContainer>
    );
};

export interface VerificationCodeInputProps extends React.ComponentProps<typeof VerificationCodeInput> {
    as?: React.ElementType;
}

VerificationCodeInput.displayName = "VerificationCodeInput";